import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Star, Check } from "lucide-react";
import { createReview } from "@/lib/api";
import { toast } from "sonner";

function StarRatingInput({ rating, onRatingChange }) {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onRatingChange(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className="p-1 transition-transform hover:scale-110"
          data-testid={`star-${star}`}
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              star <= (hoverRating || rating)
                ? "text-[var(--liturgical-secondary)] fill-[#D4AF37]"
                : "text-[#E5E0D5]"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export function ReviewDialog({ order, open, onClose, onReviewSubmitted }) {
  const [rating, setRating] = useState(0);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setSubmitting(true);
    try {
      await createReview(order.order_id, { rating, content: content.trim() || null });
      toast.success("Review submitted! Thank you for your feedback.");
      onReviewSubmitted();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    }
    setSubmitting(false);
  };

  const ratingLabels = {
    1: "Poor",
    2: "Fair",
    3: "Good",
    4: "Very Good",
    5: "Excellent"
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
            Leave a Review
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Item Info */}
          <div className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-lg">
            {order?.listing?.image_url && (
              <img
                src={order.listing.image_url}
                alt={order.listing.title}
                className="w-12 h-12 rounded object-cover"
              />
            )}
            <div>
              <p className="font-['Nunito'] font-semibold text-sm text-[#2D2A26]">
                {order?.listing?.title}
              </p>
              <p className="text-xs text-[#6B665E] font-['Nunito']">
                Purchased from {order?.seller?.name}
              </p>
            </div>
          </div>

          {/* Rating */}
          <div className="text-center space-y-2">
            <p className="font-['Nunito'] text-sm text-[#2D2A26]">
              How was your experience?
            </p>
            <StarRatingInput rating={rating} onRatingChange={setRating} />
            {rating > 0 && (
              <p className="text-sm font-['Nunito'] text-[var(--liturgical-secondary)] font-semibold">
                {ratingLabels[rating]}
              </p>
            )}
          </div>

          {/* Review Text */}
          <div className="space-y-2">
            <label className="font-['Nunito'] text-sm text-[#2D2A26]">
              Share your experience (optional)
            </label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Tell others about your experience with this seller..."
              className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[100px]"
              data-testid="review-content"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-[#E5E0D5] font-['Nunito']"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
            data-testid="submit-review-btn"
          >
            {submitting ? "Submitting..." : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReviewButton({ order, hasReviewed, onReviewSubmitted }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (hasReviewed) {
    return (
      <Button
        size="sm"
        variant="outline"
        disabled
        className="border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] font-['Nunito'] text-xs h-7"
      >
        <Check className="w-3 h-3 mr-1" /> Reviewed
      </Button>
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setDialogOpen(true)}
        className="border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)] hover:bg-[var(--liturgical-secondary)]/10 font-['Nunito'] text-xs h-7"
        data-testid={`review-btn-${order.order_id}`}
      >
        <Star className="w-3 h-3 mr-1" /> Leave Review
      </Button>
      <ReviewDialog
        order={order}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onReviewSubmitted={onReviewSubmitted}
      />
    </>
  );
}
